import { useLocalStorage } from '@vueuse/core';
import type { Ref } from 'vue';

// Module-level singletons so the panel and any indicator/control consumer share
// ONE reactive open/active-tab state within a document. Two separate
// `useLocalStorage` calls on the same key do not sync same-document (the
// `storage` event only fires cross-document), so a shared module ref is what
// lets a pill indicator open the panel and have the mounted panel react.
// These reuse the persistence keys the panel has always used.
const isOpen = useLocalStorage<boolean>('dev-tools-panel.open', false);
const activeTab = useLocalStorage<string>('dev-tools-panel.active-tab', '');

export interface DevToolsPanelControl {
  /** Reactive open state of the panel. */
  isOpen: Ref<boolean>;
  /** Reactive id of the active tab section. */
  activeTab: Ref<string>;
  /** Open the panel. */
  open(): void;
  /** Close the panel. */
  close(): void;
  /** Toggle the panel open/closed. */
  toggle(): void;
  /** Open the panel and focus the tab section with the given id. */
  openSection(id: string): void;
}

/**
 * Control surface for the DevTools panel, backed by the same persisted state the
 * panel renders from. Lets a pill indicator (or any consumer) open the panel and
 * deep-link to a section without prop-drilling or provide/inject.
 */
export function useDevToolsPanel(): DevToolsPanelControl {
  return {
    isOpen,
    activeTab,
    open: () => {
      isOpen.value = true;
    },
    close: () => {
      isOpen.value = false;
    },
    toggle: () => {
      isOpen.value = !isOpen.value;
    },
    openSection: (id: string) => {
      isOpen.value = true;
      activeTab.value = id;
    },
  };
}
